import { FeedbackLine } from "../ui/FeedbackLine";
import { useState } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Group,
  Paper,
  PasswordInput,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { hasLength, isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconKey,
  IconMail,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from "@tabler/icons-react";
import {
  useConfirmUser,
  useCreateUser,
  useDeleteUser,
  useResendUserConfirmation,
  useSetUserPassword,
  useUpdateUser,
  useUsers,
} from "../api/admin";
import { ApiError } from "../api/client";
import { useMe, usePublicSettings } from "../api/queries";
import { MIN_PASSWORD_LENGTH, type Role, type User } from "../api/types";
import { formatDateTime } from "../time/format";
import { useZone } from "../time/useZone";
import { Chip } from "../ui/Chip";
import { confirm as confirmAction } from "../ui/confirm";
import { StatePill } from "../ui/StatePill";

/**
 * The password rule, shared by the two forms on this page.
 *
 * Empty and too-short are separate messages because they are separate mistakes: one is a field
 * nobody filled in, the other a choice the server will refuse.
 */
function passwordRule(t: TFunction) {
  return (value: string) =>
    isNotEmpty(t("usersPage.validation.passwordRequired"))(value) ??
    hasLength(
      { min: MIN_PASSWORD_LENGTH },
      t("usersPage.validation.passwordTooShort", { min: MIN_PASSWORD_LENGTH }),
    )(value);
}

const ROLES: readonly Role[] = ["viewer", "editor", "admin"];

/**
 * The role choices, built per render rather than held in a module-level constant: the copy
 * lives in the catalogue, and translating at import time would freeze the language.
 */
function useRoleOptions(): readonly { value: Role; label: string }[] {
  const { t } = useTranslation("admin");
  return ROLES.map((value) => ({
    value,
    label: t(`usersPage.roleName.${value}`),
  }));
}

type Status = "active" | "pending" | "disabled" | "unconfirmed";

/**
 * What state an account is in, derived rather than stored.
 *
 * There is no "pending" column on purpose: an account waiting on its first confirmation is
 * exactly inactive with no verified address, and a disabled one is inactive with a verified
 * address. Storing the distinction separately would let the two disagree.
 */
export function statusOf(user: User): Status {
  if (!user.active) return user.email_verified_at ? "disabled" : "pending";
  // Active with an unproven address: an admin either confirmed it by hand or created the
  // account outright. Only worth flagging when there is an address that never came back.
  if (user.email && !user.email_verified_at) return "unconfirmed";
  return "active";
}

export function UsersPage() {
  const { data, isLoading } = useUsers();
  const { data: me } = useMe();
  const settings = usePublicSettings();
  const { t } = useTranslation("admin");

  if (isLoading)
    return (
      <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
        <Skeleton height={24} width={200} mb={6} />
        <Skeleton height={14} width="60ch" mb={14} />
        <Skeleton height={70} mb={20} />
        <Stack gap={6}>
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <Skeleton key={row} height={20} />
          ))}
        </Stack>
      </Paper>
    );
  const users = data ?? [];

  return (
    <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Title order={2} fz="lg" lh={1.5} mb={14}>
        {t("usersPage.heading")}
      </Title>

      <NewUserForm />

      <Box style={{ overflowX: "auto" }}>
        <Table miw={1400} verticalSpacing="md" styles={{ td: { verticalAlign: "top" } }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("usersPage.columnUser")}</Table.Th>
              <Table.Th>{t("usersPage.email")}</Table.Th>
              <Table.Th w={190}>{t("usersPage.columnStatus")}</Table.Th>
              <Table.Th w={140}>{t("usersPage.role")}</Table.Th>
              <Table.Th>{t("usersPage.columnLastLogin")}</Table.Th>
              <Table.Th w={540}>{t("usersPage.accountActions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isSelf={user.id === me?.id}
                mailConfigured={settings.data?.mail_enabled ?? false}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    </Paper>
  );
}

function UserRow({
  user,
  isSelf,
  mailConfigured,
}: {
  user: User;
  isSelf: boolean;
  mailConfigured: boolean;
}) {
  const update = useUpdateUser();
  const confirm = useConfirmUser();
  const resend = useResendUserConfirmation();
  const remove = useDeleteUser();
  const setPassword = useSetUserPassword();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [resetting, { toggle: toggleReset, close: doneReset }] = useDisclosure(false);
  const { t } = useTranslation("admin");
  const zone = useZone();
  const roleOptions = useRoleOptions();
  const busy =
    update.isPending ||
    confirm.isPending ||
    resend.isPending ||
    remove.isPending ||
    setPassword.isPending ||
    confirmingDelete;

  const status = statusOf(user);
  const statusLabel = t(`usersPage.status.${status}`);
  // The server's _guard_last_admin is the real boundary -- this only stops the obvious
  // click, and says why rather than silently doing nothing.
  const selfNote = isSelf ? t("usersPage.selfNote") : undefined;

  const run = async (action: Promise<unknown>, ok?: string) => {
    setError(null);
    setNote(null);
    try {
      await action;
      if (ok) setNote(ok);
    } catch (caught) {
      // Shown verbatim. The server owns these strings -- "cannot remove the last active
      // admin" is its rule, and paraphrasing it here is how the two drift apart.
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    }
  };

  const deleteAccount = async () => {
    if (busy || isSelf) return;
    setConfirmingDelete(true);
    try {
      const approved = await confirmAction(
        t("usersPage.deleteWarning", { username: user.username }),
        t("usersPage.deletePermanently"),
        true,
      );
      if (approved) await run(remove.mutateAsync(user.id));
    } finally {
      setConfirmingDelete(false);
    }
  };

  return (
    <>
      <Table.Tr>
        <Table.Td>{user.username}</Table.Td>
        <Table.Td c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
          {user.email ?? "—"}
        </Table.Td>
        <Table.Td>
          <Group gap={6} style={{ width: "max-content" }}>
            {status === "unconfirmed" && (
              <StatePill tone="quiet">{t("usersPage.status.active")}</StatePill>
            )}
            <StatePill tone={status === "active" ? "quiet" : "live"} title={statusLabel}>
              {statusLabel}
            </StatePill>
          </Group>
        </Table.Td>
        <Table.Td>
          <Select
            value={user.role}
            data={roleOptions}
            aria-label={t("usersPage.roleForUser", { username: user.username })}
            disabled={isSelf || busy}
            title={selfNote}
            onChange={(role) => {
              if (role) void run(update.mutateAsync({ id: user.id, role: role as Role }));
            }}
          />
        </Table.Td>
        <Table.Td c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
          {user.last_login
            ? formatDateTime(Date.parse(user.last_login), zone.name)
            : t("usersPage.neverLoggedIn")}
        </Table.Td>
        <Table.Td>
          <Stack gap={10} w={508}>
            <Box style={{ display: "grid", gridTemplateColumns: "380px 112px", gap: 16 }}>
              <Button.Group
                aria-label={t("usersPage.accountActions")}
                style={{ display: "grid", gridTemplateColumns: "160px 220px" }}
              >
                <Chip
                  variant={user.active ? "warning" : "success"}
                  leftSection={
                    user.active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />
                  }
                  disabled={isSelf || busy}
                  title={selfNote}
                  onClick={() =>
                    void run(update.mutateAsync({ id: user.id, active: !user.active }))
                  }
                >
                  {user.active ? t("usersPage.deactivate") : t("usersPage.activate")}
                </Chip>
                <Chip disabled={busy} leftSection={<IconKey size={14} />} onClick={toggleReset}>
                  {t("usersPage.resetPassword")}
                </Chip>
              </Button.Group>
              <Chip
                variant="danger"
                leftSection={<IconTrash size={14} />}
                disabled={isSelf || busy}
                title={isSelf ? t("usersPage.cannotDeleteSelf") : undefined}
                onClick={() => void deleteAccount()}
              >
                {remove.isPending ? t("usersPage.deleting") : t("usersPage.delete")}
              </Chip>
            </Box>
            {status === "pending" && (
              <Box>
                <Text size="xs" c="dimmed" mb={4}>
                  {t("usersPage.emailConfirmation")}
                </Text>
                <Button.Group
                  aria-label={t("usersPage.emailConfirmation")}
                  style={{ display: "grid", gridTemplateColumns: "200px 180px", width: 380 }}
                >
                  <Chip
                    disabled={!mailConfigured || busy}
                    leftSection={<IconMail size={14} />}
                    title={mailConfigured ? undefined : t("usersPage.mailNotConfigured")}
                    onClick={() =>
                      void run(resend.mutateAsync(user.id), t("usersPage.confirmationSent"))
                    }
                  >
                    {t("usersPage.resendEmail")}
                  </Chip>
                  <Chip
                    variant="success"
                    disabled={busy}
                    leftSection={<IconCheck size={14} />}
                    onClick={() => void run(confirm.mutateAsync(user.id))}
                  >
                    {t("usersPage.confirmManually")}
                  </Chip>
                </Button.Group>
              </Box>
            )}
          </Stack>
        </Table.Td>
      </Table.Tr>

      {resetting && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <PasswordReset
              user={user}
              submit={(password) => setPassword.mutateAsync({ id: user.id, password })}
              isPending={busy}
              onDone={(message) => {
                doneReset();
                setNote(message);
              }}
            />
          </Table.Td>
        </Table.Tr>
      )}

      <Table.Tr>
        <Table.Td colSpan={6} py={0}>
          <FeedbackLine role={error ? "alert" : "status"} c={error ? "danger" : "accent"}>
            {error ?? note}
          </FeedbackLine>
        </Table.Td>
      </Table.Tr>
    </>
  );
}

function PasswordReset({
  user,
  submit,
  isPending,
  onDone,
}: {
  user: User;
  submit: (password: string) => Promise<void>;
  isPending: boolean;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation("admin");
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { password: "" },
    validate: { password: passwordRule(t) },
  });

  return (
    /* Wraps for the same reason the new-user fields do, and one of its own: the failure banner
       is a block, and without wrapping it would compress the password field beside it rather
       than drop below it. */
    <Box
      component="form"
      display="flex"
      pt={4}
      pb={10}
      style={{ flexWrap: "wrap", alignItems: "flex-end", gap: 10 }}
      onSubmit={form.onSubmit(({ password }) => {
        setError(null);
        submit(password)
          .then(() => {
            form.reset();
            // Worth saying out loud: the server ends every other session for this account,
            // so anyone signed in as them is about to be signed out.
            onDone(t("usersPage.passwordChanged", { username: user.username }));
          })
          .catch((caught: unknown) => {
            // The server's own sentence, kept apart from the field errors above: one says the
            // typing was wrong, the other says the request was.
            setError(caught instanceof ApiError ? caught.detail : String(caught));
          });
      })}
    >
      {/* An explicit width: a `PasswordInput`'s input is a flex child of the row its visibility
          toggle sits in, so in an auto-width flex line it collapses to the eye and nothing
          else. Wider than the new-user field because the label carries a username. */}
      <PasswordInput
        label={t("usersPage.newPasswordFor", { username: user.username })}
        autoComplete="new-password"
        w={240}
        key={form.key("password")}
        {...form.getInputProps("password")}
      />
      {/* Centred against the input rather than flush with its bottom: the pill is 26px against
          the field's 36px, and a shared bottom edge reads as a button that has slipped. */}
      <Box h={36} display="flex" style={{ alignItems: "center" }}>
        <Chip type="submit" variant="accent" disabled={isPending}>
          {isPending ? t("common.saving") : t("usersPage.setPassword")}
        </Chip>
      </Box>
      <FeedbackLine role="alert" w="100%">
        {error}
      </FeedbackLine>
    </Box>
  );
}

function NewUserForm() {
  const create = useCreateUser();
  const { t } = useTranslation("admin");
  const roleOptions = useRoleOptions();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { username: "", email: "", password: "", role: "viewer" as Role },
    validate: {
      username: isNotEmpty(t("usersPage.validation.usernameRequired")),
      // Optional here, and `isEmail` refuses the empty string, so the blank case is answered
      // before it is ever asked.
      email: (value) =>
        value === "" ? null : isEmail(t("usersPage.validation.emailInvalid"))(value),
      password: passwordRule(t),
    },
  });

  return (
    <Box
      component="form"
      m="0 0 20px"
      onSubmit={form.onSubmit(({ username, email, password, role }) => {
        setError(null);
        create
          .mutateAsync({ username, password, role, email: email || undefined })
          .then(() => form.reset())
          .catch((caught: unknown) => {
            // The server's refusal, not a field's: it stays in its own banner rather than
            // being attributed to whichever input looks most likely.
            setError(caught instanceof ApiError ? caught.detail : String(caught));
          });
      })}
    >
      <Title order={3} fz="sm" lh={1.5} mb={10}>
        {t("usersPage.newUserHeading")}
      </Title>
      {/* Wraps rather than scrolls: four fields and a button do not fit a narrow admin column,
          and a horizontal scrollbar inside a form is worse than a second row. */}
      <Group gap={10} align="flex-end">
        <TextInput
          label={t("usersPage.username")}
          maxLength={64}
          key={form.key("username")}
          {...form.getInputProps("username")}
        />
        <TextInput
          label={
            <Trans
              t={t}
              i18nKey="usersPage.emailOptional"
              components={{
                optional: <Box component="span" opacity={0.7} />,
              }}
            />
          }
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
        {/* An explicit width for the same reason as the reset field above. */}
        <PasswordInput
          label={t("usersPage.password")}
          autoComplete="new-password"
          w={180}
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
        <Select
          label={t("usersPage.role")}
          data={roleOptions}
          key={form.key("role")}
          {...form.getInputProps("role")}
        />
        {/* Centred on the fields, as in the reset row above. */}
        <Box h={36} display="flex" style={{ alignItems: "center" }}>
          <Chip type="submit" variant="accent" disabled={create.isPending}>
            {create.isPending ? t("usersPage.creating") : t("usersPage.create")}
          </Chip>
        </Box>
      </Group>
      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Box>
  );
}
